import { Injectable, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs';
import io from 'socket.io-client';
import { environment } from 'src/environments/environment';
import { RegisterName } from './wsSupporting/wsTypes';
import { EmitResponse, EmitResponseWithSource } from './wsSupporting/wsEntities';
import { LocalService } from '../local.service';

@Injectable({
  providedIn: 'root'
})
export class WsUserService implements OnDestroy {
  private sockets: any[] = [];

  constructor(private localService: LocalService) {
    this.initializeSockets();
  }

  // -------------------------------
  // INITIALIZE SOCKET CONNECTIONS
  // -------------------------------
  private initializeSockets(): void {
    environment.url.lbs_services.forEach((url, index) => {

      const socket = io(url, {
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 2000,
        timeout: 5000
      });

      this.sockets[index] = socket;

      // Connected
      socket.on('connect', () => {
        //console.log(`[Socket ${index}] connected`);
        this.autoRegister(index);
      });

      // Disconnected
      socket.on('disconnect', reason => {
        //console.warn(`[Socket ${index}] disconnected:`, reason);
      });

      // Connection error
      socket.on('connect_error', err => {
        //console.error(`[Socket ${index}] connection error:`, err.message);
      });
    });
  }

  // -------------------------------
  // AUTO REGISTER ON CONNECT
  // -------------------------------
  private autoRegister(index: number): void {
    const userId = this.localService.selected?.user;

    if (!userId || userId.length <= 5) {
      return;
    }

    const businessIds: string[] =
      this.localService.loggedMember?.businesses
        ?.filter(x => x.v5 === 'BU')
        ?.map(x => x.key) ?? [];

    const countryCode = this.localService.selected?.countryCode;

    this.registerUser(userId, businessIds, countryCode, index);
  }

  // -------------------------------
  // REGISTER USER (SAFE EMIT)
  // -------------------------------
  registerUser(
    userId: string,
    businessIds: string[],
    countryCode: string,
    index?: number
  ): void {

    const emitRegister = (socket: any | undefined, idx: number) => {
      if (!socket) {
        //console.warn(`[Socket ${idx}] not initialized`);
        return;
      }

      if (!socket.connected) {
        //console.warn(`[Socket ${idx}] not connected, skipping register`);
        return;
      }

      socket.emit(
        RegisterName.UserRegisterEvent,
        userId,
        businessIds,
        countryCode
      );

      //console.log(`[Socket ${idx}] user registered`);
    };

    if (index !== undefined) {
      emitRegister(this.sockets[index], index);
    } else {
      this.sockets.forEach((s, i) => emitRegister(s, i));
    }
  }

  // -------------------------------
  // MANUAL DISCONNECT (LOGOUT)
  // -------------------------------
  disconnectAll(): void {
    this.sockets.forEach((socket, index) => {
      if (socket?.connected) {
        socket.disconnect();
        //console.log(`[Socket ${index}] manually disconnected`);
      }
    });
  }

  // -------------------------------
  // CLEANUP
  // -------------------------------
  ngOnDestroy(): void {
    this.disconnectAll();
  }

  onEmitDetected(registerName: RegisterName): Observable<EmitResponseWithSource> {
    return new Observable<EmitResponseWithSource>((observer) => {

      // Store handlers per socket for cleanup
      const handlers: Array<{
        socket: any;
        handler: (res: EmitResponse) => void;
      }> = [];

      // Attach listener to each available socket
      this.sockets.forEach((socket, index) => {
        if (!socket) {
          return;
        }

        const handler = (res: EmitResponse) => {
          observer.next({
            ...res,
            socketIndex: index
          });
        };

        socket.on(registerName, handler);
        handlers.push({ socket, handler });
      });

      // -------------------------------
      // CLEANUP ON UNSUBSCRIBE
      // -------------------------------
      return () => {
        handlers.forEach(({ socket, handler }) => {
          if (socket) {
            socket.off(registerName, handler);
          }
        });
      };
    });
  }

}